export type EquipmentType = 'machine' | 'dumbbell' | 'barbell' | 'bodyweight' | 'cable' | 'kettlebell' | 'bands' | 'smith_machine' | 'treadmill' | 'stationary_bike' | 'elliptical' | 'rowing_machine' | 'stair_climber' | 'ski_erg' | 'assault_bike' | 'other' | 'none';

export interface Exercise {
  id: string;
  name: string;
  equipment: EquipmentType;
  sets: number;
  reps: string; // e.g., "10-12" or "30 sec" or "20:00" for cardio
  trackingType?: 'reps' | 'time' | 'cardio'; // defaults to 'reps', 'cardio' for cardio exercises
  minReps?: number;
  maxReps?: number;
  notes?: string;
}

export interface WorkoutSection {
  id: string;
  title: 'Warm Up' | 'Main Workout' | 'Accessory Work' | 'Cooldown';
  duration: string; // e.g., "10 min"
  spotifyLink?: string; // Legacy - now using program-level spotifyPlaylistUrl
  exercises: Exercise[];
}

// A reusable workout session (e.g., "Push Day", "Pull Day", "Full Body")
export interface WorkoutSession {
  id: string;
  name: string; // e.g., "Push Day", "Pull Day", "Leg Day"
  sections: WorkoutSection[];
}

// Defines a workout instance within a week's schedule
export interface ScheduledWorkout {
  sessionId: string; // references WorkoutSession.id
  dayLabel?: string; // optional: "Monday", "Day 1", etc.
}

// Weekly schedule - defines which workouts happen each week (1-6 per week)
export interface WeekSchedule {
  week: number;
  workouts: ScheduledWorkout[];
}

export interface Program {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | string;
  duration: string; // "45-60 min"
  totalWeeks: number;
  image: string; // url
  spotifyPlaylistUrl?: string; // Program-level Spotify playlist
  // Legacy: single workout per week (for backward compatibility)
  sections?: WorkoutSection[];
  // New: multiple workouts per week with reusable sessions
  sessions?: WorkoutSession[];
  schedule?: WeekSchedule[];
  // Database fields
  badgeStatus?: string | null;
  experienceLevel?: string | null;
  muscleGroups?: string[] | null;
  createdBy?: string | null;
  status?: string;
  programType?: string; // 'curated', 'custom', 'quick_session', etc.
}

// Helper to get workouts for a specific week
export function getWeekWorkouts(program: Program, week: number): { session: WorkoutSession; dayLabel?: string }[] {
  // If program uses new schedule format
  if (program.sessions && program.schedule) {
    const weekSchedule = program.schedule.find(s => s.week === week);
    if (weekSchedule) {
      return weekSchedule.workouts.map(sw => ({
        session: program.sessions!.find(s => s.id === sw.sessionId)!,
        dayLabel: sw.dayLabel
      })).filter(w => w.session);
    }
  }
  
  // Legacy format: single workout repeated
  if (program.sections) {
    return [{
      session: {
        id: 'default',
        name: program.title,
        sections: program.sections
      }
    }];
  }
  
  return [];
}

// Get total workout count for a week
export function getWorkoutsPerWeek(program: Program, week: number): number {
  return getWeekWorkouts(program, week).length;
}

export const PROGRAMS: Program[] = [
  {
    id: "1",
    title: "Full Body Ignite",
    description: "High-intensity full body session to torch calories and build strength.",
    difficulty: "Intermediate",
    duration: "45 min",
    totalWeeks: 8,
    image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&q=80&w=1000",
    spotifyPlaylistUrl: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
    sections: [
      {
        id: "s1",
        title: "Warm Up",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
        exercises: [
          { id: "e1", name: "Jumping Jacks", equipment: "bodyweight", sets: 3, reps: "0:30", trackingType: "time" },
          { id: "e2", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
        ]
      },
      {
        id: "s2",
        title: "Main Workout",
        duration: "35 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
        exercises: [
          { id: "e3", name: "Goblet Squats", equipment: "dumbbell", sets: 4, reps: "12", minReps: 10, maxReps: 15 },
          { id: "e4", name: "Push Ups", equipment: "bodyweight", sets: 4, reps: "15", minReps: 12, maxReps: 20 },
          { id: "e5", name: "Lat Pulldowns", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
          { id: "e6", name: "Deadlifts", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
        ]
      },
      {
        id: "s3",
        title: "Cooldown",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
        exercises: [
          { id: "e7", name: "Static Stretching", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
        ]
      }
    ]
  },
  {
    id: "2",
    title: "Upper Body Power",
    description: "Focus on chest, back, and arms with heavy compound movements.",
    difficulty: "Advanced",
    duration: "60 min",
    totalWeeks: 12,
    image: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&q=80&w=1000",
    sections: [
      {
        id: "s1",
        title: "Warm Up",
        duration: "10 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX0XUsuxWHRQd",
        exercises: [
          { id: "e1", name: "Band Pull Aparts", equipment: "bodyweight", sets: 3, reps: "15" },
        ]
      },
      {
        id: "s2",
        title: "Main Workout",
        duration: "45 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
        exercises: [
          { id: "e2", name: "Bench Press", equipment: "barbell", sets: 5, reps: "5" },
          { id: "e3", name: "Bent Over Rows", equipment: "barbell", sets: 4, reps: "8" },
        ]
      },
      {
        id: "s3",
        title: "Cooldown",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
        exercises: [
          { id: "e4", name: "Shoulder Stretch", equipment: "bodyweight", sets: 1, reps: "2:00", trackingType: "time" },
        ]
      }
    ]
  },
  {
    id: "3",
    title: "Push Pull Legs",
    description: "Classic 3-day split targeting push, pull, and leg movements for balanced muscle development.",
    difficulty: "Intermediate",
    duration: "45-60 min",
    totalWeeks: 8,
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80&w=1000",
    sessions: [
      {
        id: "push",
        name: "Push Day",
        sections: [
          {
            id: "push-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "pw1", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
              { id: "pw2", name: "Band Pull Aparts", equipment: "bodyweight", sets: 2, reps: "15" },
            ]
          },
          {
            id: "push-main",
            title: "Main Workout",
            duration: "40 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "p1", name: "Bench Press", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "p2", name: "Overhead Press", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "p3", name: "Incline Dumbbell Press", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "p4", name: "Lateral Raises", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "p5", name: "Tricep Pushdowns", equipment: "cable", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
            ]
          },
          {
            id: "push-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "pc1", name: "Chest Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "pull",
        name: "Pull Day",
        sections: [
          {
            id: "pull-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "plw1", name: "Cat-Cow Stretch", equipment: "bodyweight", sets: 2, reps: "10" },
              { id: "plw2", name: "Band Pull Aparts", equipment: "bodyweight", sets: 2, reps: "15" },
            ]
          },
          {
            id: "pull-main",
            title: "Main Workout",
            duration: "40 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "pl1", name: "Deadlifts", equipment: "barbell", sets: 4, reps: "5-6", minReps: 5, maxReps: 6 },
              { id: "pl2", name: "Lat Pulldowns", equipment: "cable", sets: 4, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "pl3", name: "Seated Rows", equipment: "cable", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "pl4", name: "Face Pulls", equipment: "cable", sets: 3, reps: "15-20", minReps: 15, maxReps: 20 },
              { id: "pl5", name: "Barbell Curls", equipment: "barbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
            ]
          },
          {
            id: "pull-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "plc1", name: "Back Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "legs",
        name: "Leg Day",
        sections: [
          {
            id: "legs-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "lw1", name: "Leg Swings", equipment: "bodyweight", sets: 2, reps: "15 each" },
              { id: "lw2", name: "Bodyweight Squats", equipment: "bodyweight", sets: 2, reps: "15" },
            ]
          },
          {
            id: "legs-main",
            title: "Main Workout",
            duration: "45 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "l1", name: "Squats", equipment: "barbell", sets: 4, reps: "6-8", minReps: 6, maxReps: 8 },
              { id: "l2", name: "Romanian Deadlifts", equipment: "barbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "l3", name: "Leg Press", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "l4", name: "Leg Curls", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "l5", name: "Calf Raises", equipment: "machine", sets: 4, reps: "15-20", minReps: 15, maxReps: 20 },
            ]
          },
          {
            id: "legs-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "lc1", name: "Quad Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
              { id: "lc2", name: "Hamstring Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      }
    ],
    schedule: [
      { week: 1, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 2, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 3, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 4, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 5, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 6, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 7, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
      { week: 8, workouts: [{ sessionId: "push", dayLabel: "Day 1" }, { sessionId: "pull", dayLabel: "Day 2" }, { sessionId: "legs", dayLabel: "Day 3" }] },
    ]
  },
  {
    id: "4",
    title: "Upper/Lower Split",
    description: "4-day program alternating between upper and lower body for balanced strength gains.",
    difficulty: "Intermediate",
    duration: "50-60 min",
    totalWeeks: 6,
    image: "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?auto=format&fit=crop&q=80&w=1000",
    sessions: [
      {
        id: "upper",
        name: "Upper Body",
        sections: [
          {
            id: "upper-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "uw1", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
              { id: "uw2", name: "Push Ups", equipment: "bodyweight", sets: 2, reps: "10" },
            ]
          },
          {
            id: "upper-main",
            title: "Main Workout",
            duration: "45 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "u1", name: "Bench Press", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "u2", name: "Bent Over Rows", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "u3", name: "Overhead Press", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "u4", name: "Lat Pulldowns", equipment: "cable", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "u5", name: "Dumbbell Curls", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "u6", name: "Tricep Dips", equipment: "bodyweight", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
            ]
          },
          {
            id: "upper-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "uc1", name: "Shoulder Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "lower",
        name: "Lower Body",
        sections: [
          {
            id: "lower-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "lw1", name: "Leg Swings", equipment: "bodyweight", sets: 2, reps: "15 each" },
              { id: "lw2", name: "Hip Circles", equipment: "bodyweight", sets: 2, reps: "10 each" },
            ]
          },
          {
            id: "lower-main",
            title: "Main Workout",
            duration: "45 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "lo1", name: "Squats", equipment: "barbell", sets: 4, reps: "6-8", minReps: 6, maxReps: 8 },
              { id: "lo2", name: "Romanian Deadlifts", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "lo3", name: "Leg Press", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "lo4", name: "Walking Lunges", equipment: "dumbbell", sets: 3, reps: "12 each", minReps: 10, maxReps: 12 },
              { id: "lo5", name: "Leg Curls", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "lo6", name: "Calf Raises", equipment: "machine", sets: 4, reps: "15-20", minReps: 15, maxReps: 20 },
            ]
          },
          {
            id: "lower-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "loc1", name: "Quad Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
              { id: "loc2", name: "Hamstring Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      }
    ],
    schedule: [
      { week: 1, workouts: [{ sessionId: "upper", dayLabel: "Day 1" }, { sessionId: "lower", dayLabel: "Day 2" }, { sessionId: "upper", dayLabel: "Day 3" }, { sessionId: "lower", dayLabel: "Day 4" }] },
      { week: 2, workouts: [{ sessionId: "upper", dayLabel: "Day 1" }, { sessionId: "lower", dayLabel: "Day 2" }, { sessionId: "upper", dayLabel: "Day 3" }, { sessionId: "lower", dayLabel: "Day 4" }] },
      { week: 3, workouts: [{ sessionId: "upper", dayLabel: "Day 1" }, { sessionId: "lower", dayLabel: "Day 2" }, { sessionId: "upper", dayLabel: "Day 3" }, { sessionId: "lower", dayLabel: "Day 4" }] },
      { week: 4, workouts: [{ sessionId: "upper", dayLabel: "Day 1" }, { sessionId: "lower", dayLabel: "Day 2" }, { sessionId: "upper", dayLabel: "Day 3" }, { sessionId: "lower", dayLabel: "Day 4" }] },
      { week: 5, workouts: [{ sessionId: "upper", dayLabel: "Day 1" }, { sessionId: "lower", dayLabel: "Day 2" }, { sessionId: "upper", dayLabel: "Day 3" }, { sessionId: "lower", dayLabel: "Day 4" }] },
      { week: 6, workouts: [{ sessionId: "upper", dayLabel: "Day 1" }, { sessionId: "lower", dayLabel: "Day 2" }, { sessionId: "upper", dayLabel: "Day 3" }, { sessionId: "lower", dayLabel: "Day 4" }] },
    ]
  },
  {
    id: "5",
    title: "HIIT Cardio Blast",
    description: "High-intensity interval training to maximize fat burn and cardiovascular endurance.",
    difficulty: "Advanced",
    duration: "30-40 min",
    totalWeeks: 4,
    image: "https://images.unsplash.com/photo-1599058917212-d750089bc07e?auto=format&fit=crop&q=80&w=1000",
    sections: [
      {
        id: "hiit-warmup",
        title: "Warm Up",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
        exercises: [
          { id: "hw1", name: "Jumping Jacks", equipment: "bodyweight", sets: 2, reps: "0:30", trackingType: "time" },
          { id: "hw2", name: "High Knees", equipment: "bodyweight", sets: 2, reps: "0:30", trackingType: "time" },
        ]
      },
      {
        id: "hiit-main",
        title: "Main Workout",
        duration: "25 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
        exercises: [
          { id: "h1", name: "Burpees", equipment: "bodyweight", sets: 4, reps: "45 sec on/15 off" },
          { id: "h2", name: "Mountain Climbers", equipment: "bodyweight", sets: 4, reps: "45 sec on/15 off" },
          { id: "h3", name: "Box Jumps", equipment: "bodyweight", sets: 4, reps: "45 sec on/15 off" },
          { id: "h4", name: "Battle Ropes", equipment: "cable", sets: 4, reps: "45 sec on/15 off" },
          { id: "h5", name: "Kettlebell Swings", equipment: "dumbbell", sets: 4, reps: "45 sec on/15 off" },
        ]
      },
      {
        id: "hiit-cool",
        title: "Cooldown",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
        exercises: [
          { id: "hc1", name: "Walking", equipment: "bodyweight", sets: 1, reps: "3:00", trackingType: "time" },
          { id: "hc2", name: "Deep Breathing", equipment: "bodyweight", sets: 1, reps: "2:00", trackingType: "time" },
        ]
      }
    ]
  },
  {
    id: "6",
    title: "Strength Foundation",
    description: "Build a solid strength base with fundamental compound movements and progressive overload.",
    difficulty: "Beginner",
    duration: "40-50 min",
    totalWeeks: 8,
    image: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?auto=format&fit=crop&q=80&w=1000",
    sections: [
      {
        id: "sf-warmup",
        title: "Warm Up",
        duration: "10 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
        exercises: [
          { id: "sfw1", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
          { id: "sfw2", name: "Bodyweight Squats", equipment: "bodyweight", sets: 2, reps: "15" },
          { id: "sfw3", name: "Cat-Cow Stretch", equipment: "bodyweight", sets: 2, reps: "10" },
        ]
      },
      {
        id: "sf-main",
        title: "Main Workout",
        duration: "30 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
        exercises: [
          { id: "sf1", name: "Goblet Squats", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
          { id: "sf2", name: "Push Ups", equipment: "bodyweight", sets: 3, reps: "8-12", minReps: 8, maxReps: 12 },
          { id: "sf3", name: "Dumbbell Rows", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
          { id: "sf4", name: "Glute Bridges", equipment: "bodyweight", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
          { id: "sf5", name: "Plank", equipment: "bodyweight", sets: 3, reps: "0:30", trackingType: "time" },
        ]
      },
      {
        id: "sf-cool",
        title: "Cooldown",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
        exercises: [
          { id: "sfc1", name: "Full Body Stretch", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
        ]
      }
    ]
  },
  {
    id: "7",
    title: "Core Crusher",
    description: "Dedicated core program to build abs, obliques, and lower back stability.",
    difficulty: "Intermediate",
    duration: "25-30 min",
    totalWeeks: 6,
    image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&q=80&w=1000",
    sections: [
      {
        id: "cc-warmup",
        title: "Warm Up",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
        exercises: [
          { id: "ccw1", name: "Cat-Cow Stretch", equipment: "bodyweight", sets: 2, reps: "10" },
          { id: "ccw2", name: "Hip Circles", equipment: "bodyweight", sets: 2, reps: "10 each" },
        ]
      },
      {
        id: "cc-main",
        title: "Main Workout",
        duration: "20 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
        exercises: [
          { id: "cc1", name: "Plank", equipment: "bodyweight", sets: 3, reps: "0:45", trackingType: "time" },
          { id: "cc2", name: "Russian Twists", equipment: "dumbbell", sets: 3, reps: "20 each", minReps: 15, maxReps: 20 },
          { id: "cc3", name: "Bicycle Crunches", equipment: "bodyweight", sets: 3, reps: "20 each", minReps: 15, maxReps: 25 },
          { id: "cc4", name: "Leg Raises", equipment: "bodyweight", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
          { id: "cc5", name: "Dead Bug", equipment: "bodyweight", sets: 3, reps: "10 each" },
          { id: "cc6", name: "Mountain Climbers", equipment: "bodyweight", sets: 3, reps: "0:30", trackingType: "time" },
        ]
      },
      {
        id: "cc-cool",
        title: "Cooldown",
        duration: "5 min",
        spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
        exercises: [
          { id: "ccc1", name: "Child's Pose", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
          { id: "ccc2", name: "Cobra Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
        ]
      }
    ]
  },
  {
    id: "8",
    title: "5-Day Bro Split",
    description: "Classic bodybuilding split targeting each muscle group once per week for maximum growth.",
    difficulty: "Advanced",
    duration: "60-75 min",
    totalWeeks: 12,
    image: "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?auto=format&fit=crop&q=80&w=1000",
    sessions: [
      {
        id: "chest",
        name: "Chest Day",
        sections: [
          {
            id: "chest-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "chw1", name: "Push Ups", equipment: "bodyweight", sets: 2, reps: "10" },
            ]
          },
          {
            id: "chest-main",
            title: "Main Workout",
            duration: "55 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "ch1", name: "Bench Press", equipment: "barbell", sets: 4, reps: "6-8", minReps: 6, maxReps: 8 },
              { id: "ch2", name: "Incline Dumbbell Press", equipment: "dumbbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "ch3", name: "Cable Flyes", equipment: "cable", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "ch4", name: "Dips", equipment: "bodyweight", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "ch5", name: "Pec Deck", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
            ]
          },
          {
            id: "chest-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "chc1", name: "Chest Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "back",
        name: "Back Day",
        sections: [
          {
            id: "back-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "baw1", name: "Band Pull Aparts", equipment: "bodyweight", sets: 2, reps: "15" },
            ]
          },
          {
            id: "back-main",
            title: "Main Workout",
            duration: "55 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "ba1", name: "Deadlifts", equipment: "barbell", sets: 4, reps: "5-6", minReps: 5, maxReps: 6 },
              { id: "ba2", name: "Pull Ups", equipment: "bodyweight", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "ba3", name: "Bent Over Rows", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "ba4", name: "Seated Rows", equipment: "cable", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "ba5", name: "Face Pulls", equipment: "cable", sets: 3, reps: "15-20", minReps: 15, maxReps: 20 },
            ]
          },
          {
            id: "back-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "bac1", name: "Back Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "shoulders",
        name: "Shoulders Day",
        sections: [
          {
            id: "sh-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "shw1", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
            ]
          },
          {
            id: "sh-main",
            title: "Main Workout",
            duration: "50 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "sh1", name: "Overhead Press", equipment: "barbell", sets: 4, reps: "6-8", minReps: 6, maxReps: 8 },
              { id: "sh2", name: "Lateral Raises", equipment: "dumbbell", sets: 4, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "sh3", name: "Rear Delt Flyes", equipment: "dumbbell", sets: 4, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "sh4", name: "Front Raises", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "sh5", name: "Shrugs", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
            ]
          },
          {
            id: "sh-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "shc1", name: "Shoulder Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "arms",
        name: "Arms Day",
        sections: [
          {
            id: "arms-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "arw1", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
            ]
          },
          {
            id: "arms-main",
            title: "Main Workout",
            duration: "50 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "ar1", name: "Barbell Curls", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "ar2", name: "Skull Crushers", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "ar3", name: "Hammer Curls", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "ar4", name: "Tricep Pushdowns", equipment: "cable", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "ar5", name: "Preacher Curls", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "ar6", name: "Overhead Tricep Extension", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
            ]
          },
          {
            id: "arms-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "arc1", name: "Arm Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "legs5",
        name: "Legs Day",
        sections: [
          {
            id: "l5-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "l5w1", name: "Leg Swings", equipment: "bodyweight", sets: 2, reps: "15 each" },
            ]
          },
          {
            id: "l5-main",
            title: "Main Workout",
            duration: "55 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "l51", name: "Squats", equipment: "barbell", sets: 4, reps: "6-8", minReps: 6, maxReps: 8 },
              { id: "l52", name: "Leg Press", equipment: "machine", sets: 4, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "l53", name: "Romanian Deadlifts", equipment: "barbell", sets: 4, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "l54", name: "Leg Extensions", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "l55", name: "Leg Curls", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "l56", name: "Calf Raises", equipment: "machine", sets: 4, reps: "15-20", minReps: 15, maxReps: 20 },
            ]
          },
          {
            id: "l5-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "l5c1", name: "Quad Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
              { id: "l5c2", name: "Hamstring Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      }
    ],
    schedule: [
      { week: 1, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 2, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 3, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 4, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 5, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 6, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 7, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 8, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 9, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 10, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 11, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
      { week: 12, workouts: [{ sessionId: "chest", dayLabel: "Day 1" }, { sessionId: "back", dayLabel: "Day 2" }, { sessionId: "shoulders", dayLabel: "Day 3" }, { sessionId: "arms", dayLabel: "Day 4" }, { sessionId: "legs5", dayLabel: "Day 5" }] },
    ]
  },
  {
    id: "9",
    title: "Athletic Performance",
    description: "Build explosive power, agility, and functional strength for sports performance.",
    difficulty: "Advanced",
    duration: "50-60 min",
    totalWeeks: 8,
    image: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&q=80&w=1000",
    sessions: [
      {
        id: "power",
        name: "Power Day",
        sections: [
          {
            id: "pow-warmup",
            title: "Warm Up",
            duration: "10 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "poww1", name: "Jump Rope", equipment: "bodyweight", sets: 2, reps: "2:00", trackingType: "time" },
              { id: "poww2", name: "Dynamic Stretches", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
            ]
          },
          {
            id: "pow-main",
            title: "Main Workout",
            duration: "40 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "pow1", name: "Box Jumps", equipment: "bodyweight", sets: 4, reps: "8", minReps: 6, maxReps: 10 },
              { id: "pow2", name: "Power Cleans", equipment: "barbell", sets: 4, reps: "5", minReps: 4, maxReps: 6 },
              { id: "pow3", name: "Medicine Ball Slams", equipment: "dumbbell", sets: 3, reps: "12", minReps: 10, maxReps: 15 },
              { id: "pow4", name: "Broad Jumps", equipment: "bodyweight", sets: 3, reps: "8", minReps: 6, maxReps: 10 },
              { id: "pow5", name: "Sled Push", equipment: "machine", sets: 3, reps: "0:30", trackingType: "time" },
            ]
          },
          {
            id: "pow-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "powc1", name: "Foam Rolling", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "strength",
        name: "Strength Day",
        sections: [
          {
            id: "str-warmup",
            title: "Warm Up",
            duration: "10 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "strw1", name: "Bodyweight Squats", equipment: "bodyweight", sets: 2, reps: "15" },
              { id: "strw2", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
            ]
          },
          {
            id: "str-main",
            title: "Main Workout",
            duration: "40 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "str1", name: "Squats", equipment: "barbell", sets: 5, reps: "5", minReps: 4, maxReps: 6 },
              { id: "str2", name: "Bench Press", equipment: "barbell", sets: 5, reps: "5", minReps: 4, maxReps: 6 },
              { id: "str3", name: "Deadlifts", equipment: "barbell", sets: 5, reps: "5", minReps: 4, maxReps: 6 },
              { id: "str4", name: "Pull Ups", equipment: "bodyweight", sets: 4, reps: "8-10", minReps: 6, maxReps: 12 },
            ]
          },
          {
            id: "str-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "strc1", name: "Static Stretching", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "agility",
        name: "Agility Day",
        sections: [
          {
            id: "ag-warmup",
            title: "Warm Up",
            duration: "10 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "agw1", name: "High Knees", equipment: "bodyweight", sets: 2, reps: "0:30", trackingType: "time" },
              { id: "agw2", name: "Butt Kicks", equipment: "bodyweight", sets: 2, reps: "0:30", trackingType: "time" },
            ]
          },
          {
            id: "ag-main",
            title: "Main Workout",
            duration: "35 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "ag1", name: "Ladder Drills", equipment: "bodyweight", sets: 4, reps: "0:30", trackingType: "time" },
              { id: "ag2", name: "Cone Drills", equipment: "bodyweight", sets: 4, reps: "0:30", trackingType: "time" },
              { id: "ag3", name: "Lateral Shuffles", equipment: "bodyweight", sets: 3, reps: "30 sec each" },
              { id: "ag4", name: "Sprint Intervals", equipment: "bodyweight", sets: 6, reps: "20 sec on/40 off" },
              { id: "ag5", name: "Plyo Push Ups", equipment: "bodyweight", sets: 3, reps: "8-10", minReps: 6, maxReps: 12 },
            ]
          },
          {
            id: "ag-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "agc1", name: "Walking", equipment: "bodyweight", sets: 1, reps: "3:00", trackingType: "time" },
              { id: "agc2", name: "Hip Flexor Stretch", equipment: "bodyweight", sets: 1, reps: "60 sec each" },
            ]
          }
        ]
      }
    ],
    schedule: [
      { week: 1, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 2, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 3, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 4, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 5, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 6, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 7, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
      { week: 8, workouts: [{ sessionId: "power", dayLabel: "Day 1" }, { sessionId: "strength", dayLabel: "Day 2" }, { sessionId: "agility", dayLabel: "Day 3" }] },
    ]
  },
  {
    id: "10",
    title: "Full Body 3x Week",
    description: "Efficient 3-day full body program hitting all muscle groups each session.",
    difficulty: "Beginner",
    duration: "45-50 min",
    totalWeeks: 8,
    image: "https://images.unsplash.com/photo-1581009137042-c552e485697a?auto=format&fit=crop&q=80&w=1000",
    sessions: [
      {
        id: "fullA",
        name: "Full Body A",
        sections: [
          {
            id: "fba-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "fbaw1", name: "Jumping Jacks", equipment: "bodyweight", sets: 2, reps: "0:30", trackingType: "time" },
              { id: "fbaw2", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
            ]
          },
          {
            id: "fba-main",
            title: "Main Workout",
            duration: "35 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "fba1", name: "Squats", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "fba2", name: "Bench Press", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "fba3", name: "Bent Over Rows", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "fba4", name: "Overhead Press", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "fba5", name: "Plank", equipment: "bodyweight", sets: 3, reps: "0:45", trackingType: "time" },
            ]
          },
          {
            id: "fba-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "fbac1", name: "Full Body Stretch", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "fullB",
        name: "Full Body B",
        sections: [
          {
            id: "fbb-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "fbbw1", name: "High Knees", equipment: "bodyweight", sets: 2, reps: "0:30", trackingType: "time" },
              { id: "fbbw2", name: "Bodyweight Squats", equipment: "bodyweight", sets: 2, reps: "15" },
            ]
          },
          {
            id: "fbb-main",
            title: "Main Workout",
            duration: "35 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "fbb1", name: "Deadlifts", equipment: "barbell", sets: 3, reps: "6-8", minReps: 6, maxReps: 8 },
              { id: "fbb2", name: "Incline Dumbbell Press", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "fbb3", name: "Lat Pulldowns", equipment: "cable", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "fbb4", name: "Lateral Raises", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "fbb5", name: "Russian Twists", equipment: "dumbbell", sets: 3, reps: "15 each", minReps: 12, maxReps: 20 },
            ]
          },
          {
            id: "fbb-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "fbbc1", name: "Static Stretching", equipment: "bodyweight", sets: 1, reps: "5:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "fullC",
        name: "Full Body C",
        sections: [
          {
            id: "fbc-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "fbcw1", name: "Leg Swings", equipment: "bodyweight", sets: 2, reps: "15 each" },
              { id: "fbcw2", name: "Push Ups", equipment: "bodyweight", sets: 2, reps: "10" },
            ]
          },
          {
            id: "fbc-main",
            title: "Main Workout",
            duration: "35 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "fbc1", name: "Goblet Squats", equipment: "dumbbell", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "fbc2", name: "Push Ups", equipment: "bodyweight", sets: 3, reps: "15-20", minReps: 12, maxReps: 20 },
              { id: "fbc3", name: "Dumbbell Rows", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "fbc4", name: "Lunges", equipment: "dumbbell", sets: 3, reps: "10 each", minReps: 8, maxReps: 12 },
              { id: "fbc5", name: "Bicycle Crunches", equipment: "bodyweight", sets: 3, reps: "20 each", minReps: 15, maxReps: 25 },
            ]
          },
          {
            id: "fbc-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "fbcc1", name: "Child's Pose", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
              { id: "fbcc2", name: "Quad Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      }
    ],
    schedule: [
      { week: 1, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 2, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 3, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 4, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 5, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 6, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 7, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
      { week: 8, workouts: [{ sessionId: "fullA", dayLabel: "Monday" }, { sessionId: "fullB", dayLabel: "Wednesday" }, { sessionId: "fullC", dayLabel: "Friday" }] },
    ]
  },
  {
    id: "11",
    title: "Upper/Lower 2-Day",
    description: "Simple 2-day split alternating upper and lower body for beginners or busy schedules.",
    difficulty: "Beginner",
    duration: "40-50 min",
    totalWeeks: 8,
    image: "https://images.unsplash.com/photo-1574680096145-d05b474e2155?auto=format&fit=crop&q=80&w=1000",
    sessions: [
      {
        id: "upper2",
        name: "Upper Body",
        sections: [
          {
            id: "upper2-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "u2w1", name: "Arm Circles", equipment: "bodyweight", sets: 2, reps: "20" },
              { id: "u2w2", name: "Push Ups", equipment: "bodyweight", sets: 2, reps: "10" },
            ]
          },
          {
            id: "upper2-main",
            title: "Main Workout",
            duration: "30 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "u21", name: "Bench Press", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "u22", name: "Bent Over Rows", equipment: "barbell", sets: 3, reps: "8-10", minReps: 8, maxReps: 10 },
              { id: "u23", name: "Overhead Press", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "u24", name: "Lat Pulldowns", equipment: "cable", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "u25", name: "Dumbbell Curls", equipment: "dumbbell", sets: 2, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "u26", name: "Tricep Pushdowns", equipment: "cable", sets: 2, reps: "12-15", minReps: 12, maxReps: 15 },
            ]
          },
          {
            id: "upper2-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "u2c1", name: "Shoulder Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
              { id: "u2c2", name: "Chest Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      },
      {
        id: "lower2",
        name: "Lower Body",
        sections: [
          {
            id: "lower2-warmup",
            title: "Warm Up",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DXpe9t58Z9pYx",
            exercises: [
              { id: "l2w1", name: "Leg Swings", equipment: "bodyweight", sets: 2, reps: "15 each" },
              { id: "l2w2", name: "Bodyweight Squats", equipment: "bodyweight", sets: 2, reps: "15" },
            ]
          },
          {
            id: "lower2-main",
            title: "Main Workout",
            duration: "30 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX76Wlfdnj7AP",
            exercises: [
              { id: "l21", name: "Goblet Squats", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "l22", name: "Romanian Deadlifts", equipment: "dumbbell", sets: 3, reps: "10-12", minReps: 10, maxReps: 12 },
              { id: "l23", name: "Walking Lunges", equipment: "dumbbell", sets: 3, reps: "10 each", minReps: 8, maxReps: 12 },
              { id: "l24", name: "Leg Curls", equipment: "machine", sets: 3, reps: "12-15", minReps: 12, maxReps: 15 },
              { id: "l25", name: "Calf Raises", equipment: "machine", sets: 3, reps: "15-20", minReps: 15, maxReps: 20 },
            ]
          },
          {
            id: "lower2-cool",
            title: "Cooldown",
            duration: "5 min",
            spotifyLink: "https://open.spotify.com/playlist/37i9dQZF1DX4sWSpwq3LiO",
            exercises: [
              { id: "l2c1", name: "Quad Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
              { id: "l2c2", name: "Hamstring Stretch", equipment: "bodyweight", sets: 1, reps: "1:00", trackingType: "time" },
            ]
          }
        ]
      }
    ],
    schedule: [
      { week: 1, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 2, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 3, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 4, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 5, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 6, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 7, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
      { week: 8, workouts: [{ sessionId: "upper2", dayLabel: "Day 1" }, { sessionId: "lower2", dayLabel: "Day 2" }] },
    ]
  }
];
